import { Component, Input, Output, ViewEncapsulation, EventEmitter, ChangeDetectionStrategy, ContentChild, TemplateRef, TrackByFunction, HostListener } from "@angular/core";
import { trigger, style, animate, transition } from "@angular/animations";
import { isPlatformServer } from "@angular/common";
import { CurrencyPipe } from "@angular/common";
import { BarVertical2DComponent, DataItem, calculateViewDimensions, ColorHelper, ChartCommonModule, AxesModule, BarChartModule } from "@swimlane/ngx-charts";
import { KilowantColorHelper } from "./KilowantColorHelper"
import { select } from "d3-selection";
import { scaleBand, scaleLinear } from 'd3-scale';

@Component({
    selector: "kilowant-charts-place-order",
    templateUrl: "../../../html/chart/kilowant-charts-place-order.html",
    styleUrls: ["../../../css/base-chart.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("animationState", [
            transition(":leave", [
                style({
                    opacity: 1,
                    transform: '*'
                }),
                animate(500, style({ opacity: 0, transform: "scale(0)" }))
            ])
        ])
    ],
    imports: [ChartCommonModule, AxesModule, BarChartModule]
})

export class KilowantBarPlaceOrderChartComponent extends BarVertical2DComponent {

  @Input() prefix:string = "";

  @Input() val:number = 0;
  @Output() changeVal = new EventEmitter<number>();

  @Input() disabledButtons = "";
  @Input() disabled = false;

  @Input() myContractRate:number = 0;
  @Input() myContractInFuture:boolean = false;

  @Input() customMax:number = 0;

  @Input() latestUtility:number|null = 0;

  @Input() utilityWarningDate: string|null = null;

  @Input() utilityAverageRate:number = 0; 
           averageColor = "#D0D0D0";

  @Output("datasetChanged") datasetChangedEmitter: EventEmitter<number> = new EventEmitter<number>();
  @Input() datasetSelector:boolean = false;
  @Input() selectedDatasets:number[] = [];

  utilityName: string = 'RI Energy';
  utilityNameForWarning: string = 'RI Energy Last Resort Service';

  BIG_MAX: number = 0.5;
  yScaleMin: number|null = null;

  extendedHeight: number = 0;

  multiRIEnergyCount: number = 0;
  multiCommunityCount: number = 0;
 
  btn: number = 0;
  btnWidth: number = 100;
  btnMargin: number = 10;

  countColumns: number;
  yScaleMargin = 0;

  colorHelper: KilowantColorHelper;
  legendData:string[] = [];
  myContractColor = "#58504eff";
  priceColor = "#1a73e8";

  dragging: number = 0;
  private draggingElement: any;

  isSSR = false;

  initiated: boolean = false;
  
  dragActionTimeout: number = 0;  

  ngOnInit() {
    this.countColumns = this.results.length;
    this.colorHelper = new KilowantColorHelper(this.colors, this.scheme, this.schemeType, ["Present Rate", "Target Price", "RI Energy", "RI Energy Last Resort Service", "RI Energy (Projected)", "NextEra Energy", "NextEra Energy (Projected)"], [this.myContractColor, this.priceColor, "#b4b1b0", "#b4b1b0", "#e6e5e5", "#34A853", "#bbe2c5"]);
    if (isPlatformServer(this.platformId)) {
      this.isSSR = true;
    }
  }

  update(): void {
    this.yScaleMax = 0;
    this.valueDomain = this.getValueDomain();
    if (this.myContractRate>0) {
       this.yScaleMax = this.myContractRate;
    }
    if (this.customMax>0 && this.customMax>this.yScaleMax) {
       this.yScaleMax = this.customMax;
    }
    if (this.latestUtility!=null && this.latestUtility>0 && this.latestUtility>this.yScaleMax) {
       this.yScaleMax = this.latestUtility;
    }
    if (this.yScaleMax == 0 && this.valueDomain[1] == 0) {
       this.yScaleMax = this.BIG_MAX;
    }
    if (this.val>this.yScaleMax && this.val>this.valueDomain[1]) {
       this.val = this.yScaleMax;
       this.changeVal.next(this.val);
    }

    this.yScaleMin = null;
    if (this.myContractRate>0 && (this.yScaleMin==null || this.myContractRate<this.yScaleMin)) {
       this.yScaleMin = this.myContractRate;
    }
    if (this.utilityAverageRate>0 && (this.yScaleMin==null || this.utilityAverageRate<this.yScaleMin)) {
       this.yScaleMin = this.utilityAverageRate;
    }

    this.yScaleMargin = this.yScaleMax - Math.max(this.val, this.myContractRate);
    this.extendedHeight = this.height;
    super.update();
    this.margin[1] = 6;
    this.margin[3] = 6;

    this.dims = calculateViewDimensions({
      width: this.width,
      height: this.height,
      margins: this.margin,
      showXAxis: this.xAxis,
      showYAxis: this.yAxis,
      xAxisHeight: this.xAxisHeight,
      yAxisWidth: this.yAxisWidth,
      showXLabel: this.showXAxisLabel,
      showYLabel: this.showYAxisLabel,
      showLegend: this.legend,
      legendType: this.schemeType,
      legendPosition: this.legendPosition
    });

    if (this.dims.xOffset!=null) {
       this.transform = `translate(${this.dims.xOffset-2} , ${this.margin[0] + this.dataLabelMaxHeight.negative})`;
    }
    this.drawDraggablePriceLine(this.val);
    if (!this.initiated) {
       this.initiated = true;
    }
    if (this.myContractRate>0) {
       this.drawMyKilowantRate(this.myContractRate);
    }
    this.refreshLegend();
  }

  getSeries(group: any): any {
    return group.series;
  }

  getYValPerPrice(val: number): number {
     if (val>0 && this.valueDomain[1]>0) {
        return this.dims.height*(this.valueDomain[1]-val)/(this.valueDomain[1]-this.valueDomain[0]);
     }
     return 0;
  }

  dragStart(event: any, dragElementPos: number): void {
     if (this.disabled) {
        return;
     }
     event.preventDefault();
     this.dragging = dragElementPos;
     this.draggingElement = event.target;
  }

  @HostListener("mousemove", ["$event"])
  onMouseMove(event: any): void {
     if (this.disabled) {
        return;
     }
     if (this.dragging>0) {
       const svgPoint = this.getSVGPoint(event, this.draggingElement, false);
       this.setPosition(this.draggingElement, { x: svgPoint.x, y: svgPoint.y  });
     }
  }

  @HostListener("window:mouseup", ["$event"])
  dragEnd(event: any): void {
     if (this.disabled) {
        return;
     }

     this.dragging = 0;
     this.draggingElement = null!;
  }

  onTouchMove(event: any, dragElementPos: number): void {
     if (this.disabled) {
        return;
     }
     this.dragging = dragElementPos;
     this.draggingElement = event.target;
     event.preventDefault();
     event.stopImmediatePropagation();
     const svgPoint = this.getSVGPoint(event, this.draggingElement, true);
     this.setPosition(this.draggingElement, { x: svgPoint.x, y: svgPoint.y  });
  }

  onTouchEnd(event: any): void {
     if (this.disabled) {
        return;
     }

     this.dragging = 0;
     this.draggingElement = null!;
  }

  drawDraggablePriceLine(val: number) {
        const color = this.priceColor;
        var y = 0;
        const w = this.dims.width;
        if (this.valueDomain[1]>0) {
           y = this.dims.height*(this.valueDomain[1]-val)/this.valueDomain[1];
        }
        if (this.dragging==0) {
            select("#price-line")
		.attr("points", this.generatePriceLine(y))
		.attr("fill", color)
		.attr("stroke", "none");
           var textWidth = 0;
           select("#price-text")
              .attr("y", y-5)
              .text((new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.5-5")).attr("x", function(d) {
                   if (this!=null) {
                      textWidth = (<SVGTextElement>this).getComputedTextLength();
                      return w-textWidth-12;
                   }
                   return w;
               });           
        }
  }

  drawMyKilowantRate(val: number): void {
     var y = 0;
     if (this.valueDomain[1]>0) {
        y = this.dims.height*(this.valueDomain[1]-val)/this.valueDomain[1];
     }
     var x = this.dims.width;
     var textWidth=0;
     if (!this.myContractInFuture) {
        select("#myContractArrow").attr("points", this.p(0, y)+this.p(10, y-10)+this.p(10, y+10));
        select("#myContractText")
        .text((new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.5-5")+"/kWh").attr("x", 15)
        .attr("y", y+5);
     } else {
        select("#myContractText")
        .text((new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.5-5")+"/kWh")
        .attr("x", function(d) {
                    if (this!=null) {
                      textWidth = (<SVGTextElement>this).getComputedTextLength();
                    }
                    return 0;
           })
        .attr("y", y+5);
        select("#myContractArrow").attr("points", this.p(textWidth+5, y-10)+this.p(textWidth+15, y)+this.p(textWidth+5, y+10));
     }     
  }

  refreshLegend(): void {
    var asterisk = "*";
    this.legendData = [];
    if (this.results.length>0) {
       for (let x = 0; x<this.results.length; x++) {
           for (let i=0; i<this.results[x].series.length; i++) {
              let testVal = this.results[x].series[i].name.replace(/\s*\(Projected\)\s*/,"");
              if (this.utilityWarningDate!=null && this.utilityWarningDate!="" && this.utilityAverageRate>0) {
                 testVal=testVal+asterisk+" - "+this.yAxisFormat(this.utilityAverageRate)+"/kWh";
              }
              if (!this.legendData.includes(testVal)) {
                 this.legendData.push(testVal);
              }
           }
       }
    }

    if (this.myContractRate>0) {
       this.legendData.push("Present Rate"+" - "+this.yAxisFormat(this.myContractRate)+"/kWh");
    }
    this.legendData.push("Target Price");
  }

  private getSVGPoint(event: any, element:any, isTouch: boolean): SVGPoint {
    const point = element.viewportElement.createSVGPoint();
    if (isTouch) {
      var touchLocation = event.targetTouches[0];
      point.x = touchLocation.clientX;
      point.y = touchLocation.clientY;
    } else {
      point.x = event.clientX;
      point.y = event.clientY;
    }

    const CTM = element.viewportElement.getScreenCTM();
    const svgPoint = point.matrixTransform(CTM.inverse());

    return svgPoint;
  }

  private setPosition(element: any, coord: { x: number, y: number }) {
    if (this.dragging == 1) {
       coord.y = coord.y - 10;
       if (coord.y<0) {
          coord.y = 0;
       }
       const w = this.dims.width;
       if (coord.y>this.dims.height || coord.y<this.yScaleMargin) {
          return;
       }
       element.setAttribute("points", this.generatePriceLine(coord.y));

       if (this.dims.height*this.valueDomain[1]!=0) {
         var val = this.valueDomain[1]-coord.y/this.dims.height*this.valueDomain[1];
         this.changeVal.next(val);
         var textWidth = 0;
         select("#price-text")
            .attr("y", coord.y-5)
            .text((new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.5-5")).attr("x", function(d) {
                 if (this!=null) {
                    textWidth = (<SVGTextElement>this).getComputedTextLength();
                    return w-textWidth-12;
                 }
                 return w;
             });
         
         select("#price-text-back")
         .attr("x", w-textWidth-20)
         .attr("y", coord.y-20)
         .attr("width", textWidth+20)
         .attr("height", 20);

      }
    }
  }

   drawPriceLine(val: number, pattern: string, color: string, id: string, y: number, leftShift: number, rightShift: number) {
     const w = this.dims.width;
     var x1 = 0;
     var x2 = w;
     if (leftShift>0) {
        x1 = this.groupPadding+leftShift*(this.getInnerScale().bandwidth()+this.groupPadding*2);
     }
     if (rightShift>0) {
        x2 = x2 - this.groupPadding-rightShift*(this.getInnerScale().bandwidth()+this.groupPadding*2);
     }
     select("#"+id+"-line"+this.prefix)
     .attr("x1", x1)
     .attr("x2", x2)
     .attr("y1", y)
     .attr("y2", y)
     .attr("stroke", color);
     var valText = pattern.replace("{val}", ""+(new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.5-5"));
     var textWidth = 0;
     var prefix = this.prefix;
     select("#"+id+"-text"+this.prefix)
     .text(valText).attr("x", function(d) {
         if (this!=null) {
            textWidth = (<SVGTextElement>this).getComputedTextLength();
            var xAvg = x1+(x2-x1-textWidth)/2;
            if (xAvg+textWidth>x2) {
               xAvg = x2-textWidth;
            }
            if (xAvg<0) {
               xAvg = 0;
            }
            select("#"+id+"-text-back"+prefix)
            .attr("x", xAvg-10)
            .attr("y", y-20)
            .attr("width", textWidth+20)
            .attr("height", 20);

            return xAvg;
         }
         return w;
     })
     .attr("y", y-5)
     .attr("fill", color);
   }

   getInnerDomain(): string[] {
     const domain = super.getInnerDomain();
     this.multiRIEnergyCount = 0;
     this.multiCommunityCount = 0;

     for (const d of domain) {
         if (this.isRIEnergy(d)) {
            this.multiRIEnergyCount++;
         }
         if (this.isCommunity(d)) {
            this.multiCommunityCount++;
         }
     }
     return domain;
   }

   getValueDomain(): [number, number] {
     const domain:number[] = [];
     for (const group of this.results) {
       for (const d of group.series) {
         if (!domain.includes(d.value) && d.value>0) {
             domain.push(d.value);
         }
       }
     }

     const min = this.yScaleMin ? Math.min(this.yScaleMin, ...domain)*0.8 : Math.min(0, ...domain)*0.8;
     const max = this.yScaleMax ? Math.max(this.yScaleMax, ...domain) : Math.max(0, ...domain);
     return [min, max];
   }

   getInnerScale(): any {
       const width = this.groupScale.bandwidth();
       const spacing = this.innerDomain.length / (width / this.barPadding + 1);
       let innerDomain = this.innerDomain;
       let multiRIEnergyCount = this.multiRIEnergyCount;
       let multiCommunityCount = this.multiCommunityCount;

       let totalElements = innerDomain.length;
       let isRIEnergy=this.isRIEnergy;
       let initModified = function() {
         let d3 = scaleBand().rangeRound([0, width*Math.max(1, totalElements/(totalElements-multiRIEnergyCount+1-multiCommunityCount+1))]).paddingInner(spacing).domain(innerDomain);       
         function d3Based(label: string) {
            if (isRIEnergy(label)) {
               return 0;
            } else {
               return d3(label);
            }
         }
         d3Based.bandwidth = function() {
           return d3.bandwidth();
       }
       return d3Based;
     }

     return initModified();
  }

  yAxisFormat(val: number): string|null {
     return (new CurrencyPipe("en-US")).transform(val,  "USD", "symbol", "1.3-3");
  }

  private generatePriceLine(y: number): string {
     const x = 0;
     const w = this.dims.width+20;
     return (this.p(x,(y-1))+this.p((x+w-20),(y-1))+this.p((x+w-20),(y-20))+this.p((x+w-22),(y-20))+this.p((x+w-11),(y-30))+this.p((x+w),(y-20))+this.p((x+w-2),(y-20))+this.p((x+w-2),(y+20))+this.p((x+w),(y+20))+this.p((x+w-11),(y+30))+this.p((x+w-22),(y+20))+this.p((x+w-20),(y+20))+this.p((x+w-20),(y+1))+this.p(x,(y+1))).trim();
  }

  private p(x: number, y: number): string {
     return x+","+y+" ";
  }

  private isRIEnergy(label: string): boolean {
     let testVal = <string>label;
     testVal = testVal.replace(/\*.*$/,"");
     testVal = testVal.replace(/\s*\-.*$/,"");

     if ((testVal=="RI Energy")||(testVal=="RI Energy (Projected)")) {
        return true;
     }
     return false;
  }

  private isCommunity(label: string): boolean {
     let testVal = <string>label;
     testVal = testVal.replace(/\*.*$/,"");
     testVal = testVal.replace(/\s*\-.*$/,"");

     if ((testVal=="NextEra Energy")||(testVal=="NextEra Energy (Projected)")) {
        return true;
     }
     return false;
  }
}